import { useCallback, useState } from 'react';

import {
  ApprovalRuleService,
  ApprovalRuleSimulateRequest,
  ApprovalMode,
  ApprovalRule,
  ApprovalCondition,
  ApprovalStep
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { getApprovalRuleConditionText } from './approvalsHelpers';
import {
  SimulationResult,
  SimulatorFormValues,
  SimulationOutcome,
  FinalGate,
  MatchedRule,
  SuppressedRule,
  EvaluationTrace,
  EvaluationTraceMatchStatus,
  MatchedRuleWrapper,
  UnmatchedRuleWithConversion,
  TransformApiResponseResult,
  UseApprovalRuleSimulatorProps,
  UseApprovalRuleSimulatorReturn
} from '../interfaces/ApprovalSimulator';

const extractApprovers = (
  steps: ApprovalStep[] = []
): Array<{ approver: { name: string } }> =>
  steps.length === 0
    ? []
    : steps.map((step) => ({ approver: { name: step.approver!.name } }));

const createConvertedAmountInfo = (
  wrapper: MatchedRuleWrapper | UnmatchedRuleWithConversion,
  originalAmount: number,
  originalCurrency: string,
  targetCurrency: string
) =>
  wrapper.convertedAmount
    ? {
        originalAmount,
        originalCurrency,
        convertedAmount: wrapper.convertedAmount,
        targetCurrency
      }
    : undefined;

const createGate = (rule: ApprovalRule, gateNumber: number): FinalGate => ({
  gateNumber,
  ruleName: rule.name ?? '',
  approvalMode: rule.flow?.approvalMode,
  approvers: extractApprovers(rule.flow?.steps || []),
  minimumApprovalsRequired: rule.flow?.minimumApprovalsRequired
});

const getRuleGroups = (matchedRules: ApprovalRule[]) => ({
  exclusiveRule: matchedRules.find((rule) => rule.exclusive) ?? null,
  fallbackRule: matchedRules.find((rule) => rule.defaultRule) ?? null,
  matchedActiveRules: matchedRules.filter(
    (rule) => !rule.exclusive && rule.active
  )
});

const getConditionSummary = (
  conditions: ApprovalCondition[],
  t: (key: string) => string
): string => {
  if (!conditions?.length) return t('approvalSimulator.trace.noConditions');
  return conditions
    .map((condition) => getApprovalRuleConditionText(condition, t))
    .join(', ');
};

// Transformation functions
const buildFinalGates = (
  matchedRules: ApprovalRule[],
  _t: (key: string) => string
): FinalGate[] => {
  const { exclusiveRule, fallbackRule, matchedActiveRules } =
    getRuleGroups(matchedRules);

  if (exclusiveRule) return [createGate(exclusiveRule, 1)];
  if (fallbackRule) return [createGate(fallbackRule, 1)];

  const hasManualRules = matchedActiveRules.some(
    (rule) => rule.flow?.approvalMode !== ApprovalMode.AUTO_APPROVE
  );

  if (hasManualRules) {
    return matchedActiveRules
      .filter((rule) => rule.flow?.approvalMode !== ApprovalMode.AUTO_APPROVE)
      .map((rule, index) => createGate(rule, index + 1));
  }

  return matchedActiveRules.map((rule, index) => createGate(rule, index + 1));
};

const buildSuppressedRules = (
  matchedRules: ApprovalRule[]
): SuppressedRule[] => {
  const { exclusiveRule } = getRuleGroups(matchedRules);
  if (!exclusiveRule || matchedRules.length <= 1) return [];

  return matchedRules
    .filter((rule) => rule.id !== exclusiveRule.id)
    .map(
      (rule): SuppressedRule => ({
        id: rule.id ?? 0,
        name: rule.name ?? '',
        priority: rule.priority ?? 0,
        approvalMode: rule.flow?.approvalMode
      })
    );
};

const detectAutoApproveOverride = (matchedRules: ApprovalRule[]): boolean => {
  const { exclusiveRule, matchedActiveRules } = getRuleGroups(matchedRules);

  if (exclusiveRule) return false;

  const hasAutoApprove = matchedActiveRules.some(
    (rule) => rule.flow?.approvalMode === ApprovalMode.AUTO_APPROVE
  );
  const hasManualRules = matchedActiveRules.some(
    (rule) => rule.flow?.approvalMode !== ApprovalMode.AUTO_APPROVE
  );

  return hasAutoApprove && hasManualRules;
};

const transformMatchedRules = (matchedRules: ApprovalRule[]): MatchedRule[] =>
  matchedRules.map((rule) => ({
    id: rule.id ?? 0,
    name: rule.name ?? '',
    approvalMode: rule.flow?.approvalMode,
    isExclusive: rule.exclusive,
    isFallback: rule.defaultRule
  }));

const buildEvaluationTraces = (
  matchedWrappers: MatchedRuleWrapper[],
  matchedRules: ApprovalRule[],
  unmatchedWrappers: UnmatchedRuleWithConversion[],
  t: (key: string) => string,
  originalAmount: number,
  originalCurrency: string,
  targetCurrency: string
): EvaluationTrace[] => {
  const currencyInfo = { originalAmount, originalCurrency, targetCurrency };

  const matchedTraces: EvaluationTrace[] = matchedWrappers.map(
    (wrapper, index) => {
      const rule = matchedRules[index];
      return {
        id: rule.id ?? 0,
        ruleName: rule.name ?? t('approvalSimulator.trace.unnamedRule'),
        status: EvaluationTraceMatchStatus.MATCHED,
        reason: t('approvalSimulator.trace.allConditionsMatched'),
        convertedAmount: createConvertedAmountInfo(
          wrapper,
          currencyInfo.originalAmount,
          currencyInfo.originalCurrency,
          currencyInfo.targetCurrency
        )
      };
    }
  );

  const unmatchedTraces: EvaluationTrace[] = unmatchedWrappers.map(
    (wrapper) => ({
      id: wrapper.id,
      ruleName: wrapper.name ?? t('approvalSimulator.trace.unnamedRule'),
      status: EvaluationTraceMatchStatus.NO_MATCH,
      reason: `${t('approvalSimulator.trace.conditionNotMet')} ${getConditionSummary([wrapper.condition], t)}`,
      convertedAmount: createConvertedAmountInfo(
        wrapper,
        currencyInfo.originalAmount,
        currencyInfo.originalCurrency,
        currencyInfo.targetCurrency
      )
    })
  );

  return [...matchedTraces, ...unmatchedTraces];
};

const determineSimulationOutcome = (
  matchedRules: ApprovalRule[]
): { outcome: SimulationOutcome; gateCount: number } => {
  const { exclusiveRule, fallbackRule, matchedActiveRules } =
    getRuleGroups(matchedRules);

  if (exclusiveRule) {
    return {
      outcome:
        exclusiveRule.flow?.approvalMode === ApprovalMode.AUTO_APPROVE
          ? SimulationOutcome.AutoApprove
          : SimulationOutcome.ManualApproval,
      gateCount: 1
    };
  }

  if (matchedActiveRules.length > 0)
    return {
      outcome: SimulationOutcome.ManualApproval,
      gateCount: matchedActiveRules.length
    };

  if (fallbackRule)
    return { outcome: SimulationOutcome.Fallback, gateCount: 1 };

  return {
    outcome:
      matchedRules.length > 0
        ? SimulationOutcome.ManualApproval
        : SimulationOutcome.ManualApproval,
    gateCount: 0
  };
};

const transformApiResponse = (
  apiResponse: any,
  t: (key: string) => string
): TransformApiResponseResult | null => {
  const data = apiResponse.data;
  if (!data) return null;

  const matchedWrappers = (data.matchedRules as MatchedRuleWrapper[]) || [];
  const matchedRules = matchedWrappers.map((item) => item.rule);
  const unmatchedWrappers =
    (data.unmatchedRules as UnmatchedRuleWithConversion[]) || [];

  if (matchedRules.length === 0 && unmatchedWrappers.length === 0) {
    return {
      summary: { outcome: SimulationOutcome.ManualApproval, gateCount: 0 },
      matchedRules: [],
      suppressedRules: [],
      finalGates: [],
      evaluationTraces: [],
      exclusiveRuleName: '',
      autoApproveOverridden: false
    };
  }

  const { exclusiveRule } = getRuleGroups(matchedRules);
  const { outcome: simulationOutcome, gateCount } =
    determineSimulationOutcome(matchedRules);

  const autoApproveOverridden = detectAutoApproveOverride(matchedRules);

  const anyWrapper = [...matchedWrappers, ...unmatchedWrappers].find(
    (wrapper) => wrapper.currency
  );
  const currencyInfo = {
    originalAmount: data.amount,
    originalCurrency: data.currency,
    targetCurrency: anyWrapper?.currency?.isoCode || 'EUR'
  };

  return {
    summary: {
      outcome: simulationOutcome,
      gateCount,
      autoApproveOverridden
    },
    matchedRules: transformMatchedRules(matchedRules),
    suppressedRules: buildSuppressedRules(matchedRules),

    finalGates: buildFinalGates(matchedRules, t),
    evaluationTraces: buildEvaluationTraces(
      matchedWrappers,
      matchedRules,
      unmatchedWrappers,
      t,
      currencyInfo.originalAmount,
      currencyInfo.originalCurrency,
      currencyInfo.targetCurrency
    ),
    exclusiveRuleName: exclusiveRule?.name ?? '',
    autoApproveOverridden
  };
};

export const useApprovalRuleSimulator = ({
  companyId
}: UseApprovalRuleSimulatorProps): UseApprovalRuleSimulatorReturn => {
  const { translate } = useTranslations();

  const [simulationResult, setSimulationResult] =
    useState<SimulationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const runSimulation = useCallback(
    async (formValues: SimulatorFormValues) => {
      if (!formValues?.amount || !formValues.currency) {
        return;
      }

      setIsLoading(true);
      setSimulationResult(null);

      try {
        const simulateRequest: ApprovalRuleSimulateRequest = {
          expenseTypeId: formValues.expenseType?.id,
          amount: formValues.amount!,
          currencyId: formValues.currency!.id!,
          counterpartyId: formValues.counterparty?.id
        };

        const response = await ApprovalRuleService.simulateApprovalRules(
          companyId,
          simulateRequest
        );
        const result = transformApiResponse(response, translate);

        if (result) {
          setSimulationResult(result);
        }
      } catch (err) {
        const apiError = err as Error;
        console.error('Simulation API error:', apiError);
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, translate]
  );

  const resetSimulation = useCallback(() => {
    setSimulationResult(null);
  }, []);

  return {
    simulationResult,
    isLoading,
    runSimulation,
    resetSimulation
  };
};
